import _extends from "@babel/runtime/helpers/extends";
import { useCallback, useState } from "react";
import { Form, Input, Select, Divider, Spin, message, Checkbox } from "antd";
import { observer } from "mobx-react";
import { __, _i } from "../../../utils";
import { PluginUpdateTermFields } from ".";
import { LearnMoreTag } from "../..";
const PLUGIN_UPDATE_FORM_LAYOUT = {
  labelCol: {
    span: 24
  },
  wrapperCol: {
    span: 24
  }
};
const PLUGIN_UPDATE_FORM_LAYOUT_MARGIN_BOTTOM = {
  marginBottom: 8
};
const PluginUpdateForm = observer(({
  onSave,
  onFailure,
  footer,
  pluginUpdate
}) => {
  const [forceFormValidateStatus, setForceFormValidateStatus] = useState(false);
  const {
    busy,
    slug,
    allowsAutoUpdates,
    needsLicenseKeys,
    licenses,
    unlicensedEntries,
    noUsageEntries,
    modifiableEntries,
    invalidKeysError,
    accountSiteUrl,
    licenseKeyHelpUrl,
    name,
    potentialNewsletterUser: {
      firstName,
      email
    },
    showBlogName,
    showNetworkWideUpdateIssueNotice
  } = pluginUpdate;
  const allowNoUsage = licenses.length > 1; // only allow "No usage" within multisite

  const initialValues = {
    licenses: licenses.map(({
      blog,
      code,
      installationType,
      hint,
      noUsage
    }) => {
      var _hint$help$match;

      return {
        blog,
        code: code || ( // Read a code from the hint
        hint ? (_hint$help$match = hint.help.match(/(\w{8}-\w{4}-\w{4}-\w{4}-\w{12})|(\w{32})/)) === null || _hint$help$match === void 0 ? void 0 : _hint$help$match[0] : "") || "",
        installationType: installationType || "",
        noUsage
      };
    }),
    autoUpdates: allowsAutoUpdates,
    terms: false,
    telemetry: false,
    newsletter: false,
    firstName,
    email
  };
  const [form] = Form.useForm();
  const [showTermFields, setShowTermFields] = useState(noUsageEntries.length !== unlicensedEntries.length);
  const handleSave = useCallback(async values => {
    const {
      licenses: formLicenses,
      ...meta
    } = values;
    const newMeta = { ...meta,
      licenses: JSON.stringify(needsLicenseKeys ? // Remove all entries which are already licensed
      formLicenses.filter(({
        blog
      }) => {
        const [foundLicense] = licenses.filter(k => k.blog === blog);
        return unlicensedEntries.indexOf(foundLicense) > -1;
      }) : // The free version does not show any inputs for license keys and license activation is done by backend
      undefined)
    };

    try {
      await pluginUpdate.update(newMeta);
      form.setFieldsValue({
        terms: false,
        telemetry: false,
        newsletter: false
      });
      message.success(__("Your license has been activated!"));
      onSave === null || onSave === void 0 ? void 0 : onSave();
    } catch (e) {
      if (!pluginUpdate.invalidKeysError) {
        message.error(e.responseJSON.message);
      } // No error needed cause it is catch trough `invalidKeysError`


      onFailure === null || onFailure === void 0 ? void 0 : onFailure();
      throw e;
    }
  }, [pluginUpdate, onSave, licenses, unlicensedEntries, needsLicenseKeys]);
  const onFinish = useCallback(async values => {
    try {
      await handleSave(values);
    } catch (e) {// Silence is golden.
    } finally {
      setForceFormValidateStatus(false);
    }
  }, [form, handleSave, setForceFormValidateStatus]);
  const onFinishFailed = useCallback(() => {
    setForceFormValidateStatus(true);
    setShowTermFields(true);
  }, []);
  return /*#__PURE__*/React.createElement(Spin, {
    spinning: busy
  }, unlicensedEntries.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
    className: "description",
    style: {
      marginBottom: 15
    }
  }, needsLicenseKeys ? __("Activate your %s PRO license to receive regular updates and support.", name) : _i(__("To use all advantages of %s {{strong}}you need a free license{{/strong}}. After license activation you will receive answers to support requests and announcements in your plugin (e.g. also notices for discount actions of the PRO version).", name), {
    strong: /*#__PURE__*/React.createElement("strong", null)
  })), showNetworkWideUpdateIssueNotice && /*#__PURE__*/React.createElement("div", {
    className: "notice notice-error inline below-h2 notice-alt",
    style: {
      margin: "0 0 10px 0"
    }
  }, /*#__PURE__*/React.createElement("p", null, __("You are using a WordPress mulisite. Due to technical limitations of WordPress core, %s can receive automatic updates in WordPress multisites only if the plugin is enabled network-wide. You can enable the plugin network-wide, but still only license it for specific sites.", name)), /*#__PURE__*/React.createElement("p", null, __("Please enable %s network-wide or take care of regular updates manually! ", name))), /*#__PURE__*/React.createElement(Form, _extends({
    name: "license-form-".concat(slug),
    id: "license-form-".concat(slug),
    form: form
  }, PLUGIN_UPDATE_FORM_LAYOUT, {
    initialValues: initialValues,
    onFinish: onFinish,
    onFinishFailed: onFinishFailed,
    onChange: () => {
      setShowTermFields(true);
    }
  }), needsLicenseKeys && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Form.List, {
    name: "licenses"
  }, fields => fields.map((field, index) => {
    const {
      blog
    } = form.getFieldValue(["licenses", field.name]);
    const [foundLicense] = licenses.filter(k => k.blog === blog); // Already licensed, do not show in form again

    if (unlicensedEntries.indexOf(foundLicense) === -1) {
      return null;
    }

    const {
      busy: licenseBusy,
      blogName,
      programmatically,
      host
    } = foundLicense;
    const lastBlogPutError = invalidKeysError === null || invalidKeysError === void 0 ? void 0 : invalidKeysError[blog]; // Hints priority: Form handling, POST-Request, Hint coming from license deactivation

    const useHint = forceFormValidateStatus ? {} : lastBlogPutError || foundLicense.hint;
    const isLicenseMaxUsagesReached = !!(lastBlogPutError !== null && lastBlogPutError !== void 0 && lastBlogPutError.debug.errors["LicenseMaxUsagesReached"]); // Modify hint for LicenseMaxUsagesReached error and show a "Learn more tag"

    let customHelp;

    if (typeof useHint !== "boolean" && isLicenseMaxUsagesReached) {
      customHelp = /*#__PURE__*/React.createElement("span", null, useHint.help, " ", /*#__PURE__*/React.createElement(LearnMoreTag, {
        url: __("https://devowl.io/knowledge-base/the-limit-of-activated-clients-for-this-license-has-already-been-reached/")
      }));
    }

    return /*#__PURE__*/React.createElement(Spin, {
      spinning: licenseBusy,
      key: field.key
    }, /*#__PURE__*/React.createElement(Form.Item, {
      noStyle: true,
      shouldUpdate: (prevValues, nextValues) => prevValues.licenses[field.key].noUsage !== nextValues.licenses[field.key].noUsage
    }, ({
      getFieldValue
    }) => {
      const noUsage = getFieldValue(["licenses", field.key, "noUsage"]);
      return /*#__PURE__*/React.createElement(Form.Item, _extends({
        label: /*#__PURE__*/React.createElement("span", null, unlicensedEntries.length > 1 || showBlogName ? _i(__("Installation type and license key for {{strong}}%s{{/strong}}", blogName), {
          strong: /*#__PURE__*/React.createElement("strong", null)
        }) : __("Installation type and license key"), " ", /*#__PURE__*/React.createElement(LearnMoreTag, {
          url: licenseKeyHelpUrl
        })),
        help: customHelp
      }, useHint, {
        required: true,
        style: PLUGIN_UPDATE_FORM_LAYOUT_MARGIN_BOTTOM
      }), !noUsage && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Form.Item, {
        fieldKey: [field.fieldKey, "code"],
        name: [field.name, "code"],
        noStyle: true,
        rules: [{
          // Allow UUID format and additionally a custom 32-length string
          pattern: /(^\w{8}-\w{4}-\w{4}-\w{4}-\w{12}$)|(^\w{32}$)/,
          required: true,
          message: __("Please enter a valid license key!")
        }]
      }, /*#__PURE__*/React.createElement(Input, {
        placeholder: programmatically ? programmatically.code : "XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX",
        disabled: !!programmatically,
        addonBefore: /*#__PURE__*/React.createElement(Form.Item, {
          fieldKey: [field.fieldKey, "installationType"],
          name: [field.name, "installationType"],
          noStyle: true,
          rules: [{
            required: true,
            message: __("Please choose an installation type!")
          }]
        }, /*#__PURE__*/React.createElement(Select, {
          placeholder: __("Installation type"),
          disabled: !!programmatically
        }, /*#__PURE__*/React.createElement(Select.Option, {
          value: "",
          disabled: true
        }, programmatically ? programmatically.type === "development" ? __("Development") : __("Production") : __("Select installation type…")), /*#__PURE__*/React.createElement(Select.Option, {
          value: "production"
        }, __("Production")), /*#__PURE__*/React.createElement(Select.Option, {
          value: "development"
        }, __("Development"))))
      })), index === 0 && /*#__PURE__*/React.createElement("p", {
        className: "description",
        style: {
          marginTop: 5
        }
      }, /*#__PURE__*/React.createElement("strong", null, __("What is an installation type?")), " ", __("You can use each license in both production and development environments."), " ", _i(__('Generally speaking, you use "{{strong}}Development{{/strong}}" when your site is not yet live, or it is a staging environment of your site. "{{strong}}Production{{/strong}}" is what you use once your site is live. You can change the installation-type at any time by deactivating the license and activate it again.'), {
        strong: /*#__PURE__*/React.createElement("strong", null)
      }))), allowNoUsage && /*#__PURE__*/React.createElement(Form.Item, {
        fieldKey: [field.fieldKey, "noUsage"],
        name: [field.name, "noUsage"],
        valuePropName: "checked",
        style: {
          marginTop: noUsage ? -25 : index === 0 ? -8 : 0,
          marginBottom: 0
        }
      }, /*#__PURE__*/React.createElement(Checkbox, null, __("I do not want to license and use the plugin for this site within my multisite."))));
    }), programmatically && /*#__PURE__*/React.createElement("div", {
      className: "notice notice-warning inline below-h2 notice-alt",
      style: {
        margin: "0 0 10px 0"
      }
    }, /*#__PURE__*/React.createElement("p", null, _i(__("This license cannot be activated manually because it is configured programmatically. That means you have used the {{a}}activation filter{{/a}} for host {{code}}%s{{/code}} (Blog ID: %d). Unfortunately, there went something wrong while activating the license.", host, blog), {
      code: /*#__PURE__*/React.createElement("code", null),
      a: /*#__PURE__*/React.createElement("a", {
        href: "https://docs.devowl.io/real-cookie-banner/hooks/DevOwl_RealProductManager_License_Programmatic_$slug.html",
        target: "_blank",
        rel: "noreferrer"
      })
    }), " ", "\u2022", " ", /*#__PURE__*/React.createElement("a", {
      className: "button-link",
      onClick: async () => {
        try {
          await foundLicense.retry();

          if (foundLicense.hint) {
            message.error(foundLicense.hint.help);
          }
        } catch (e) {
          message.error(e.responseJSON.message);
        }
      }
    }, __("Retry activation")))), isLicenseMaxUsagesReached && /*#__PURE__*/React.createElement("a", {
      href: accountSiteUrl,
      target: "_blank",
      rel: "noreferrer",
      className: "button button-primary"
    }, __("Manage licenses in the customer center")));
  })), /*#__PURE__*/React.createElement(Divider, {
    type: "horizontal",
    style: {
      margin: "10px 0"
    }
  })), modifiableEntries.length > 0 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
    style: {
      display: showTermFields ? "block" : "none"
    }
  }, /*#__PURE__*/React.createElement(PluginUpdateTermFields, {
    pluginUpdate: pluginUpdate
  })), footer))));
});
export { PluginUpdateForm, PLUGIN_UPDATE_FORM_LAYOUT, PLUGIN_UPDATE_FORM_LAYOUT_MARGIN_BOTTOM };